import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  PLATFORM_ID,
  signal,
  viewChild
} from '@angular/core';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { IFilterFormOptionValue, IFilterFormValues } from '@sondermind/data-access/public-facing/filter-form';
import { LandingPageContact } from '@sondermind/features/support-phone-number-card';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { AiConnectProjectType, SegmentService } from '@sondermind/segment';
import { IAppliedFilterChip } from '../../models/applied-filter-chip.interface';
import { IFindMyFitChip } from '../../models/chip.interface';
import { GuidedFlowFilterContext, IGuidedFlowFilterChange } from '../../models/guided-flow-analytics';
import { GuidedFeedbackResponse } from '../../models/guided-feedback-response.enum';
import {
  GUIDED_ARRAY_FILTER_KEYS,
  GUIDED_QUESTIONS,
  GUIDED_REMOVABLE_SCALAR_KEYS
} from '../../models/guided-questions.const';
import { IGuidedQuestion } from '../../models/guided-question.interface';

/** The guided flow only renders in the provider directory, always as the guided variant. */
const GUIDED_FLOW_PROJECT = AiConnectProjectType.PROVIDER_DIRECTORY;
const GUIDED_FLOW_VARIANT = 'guided';

/**
 * Guided chip provider-finder (Phase 1, frontend-only).
 *
 * Presents a static, ordered questionnaire in the directory sidebar. Each step
 * shows multi-select chips. The provider-directory filter form is the single
 * source of truth: a chip renders as selected when its value is present in the
 * current form values, and toggling a chip applies (or removes) that filter
 * immediately via `filtersApplied`. Because selection is derived from
 * `formValues`, it stays in sync when the user changes a filter in the bar and
 * persists as they navigate back and forward through the questions. "Next" and
 * "Back" only move between questions. There is no agent and no freeform text box
 * in Phase 1.
 *
 * The component fires its own guided-flow Segment events (MER-4945) through the
 * injected `SegmentService`; every track call is SSR-guarded and error-swallowing.
 */
@Component({
  standalone: true,
  selector: 'directory-companion-guided',
  templateUrl: './directory-companion-guided.component.html',
  styleUrls: ['./directory-companion-guided.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IrisIconComponent, IrisIconButtonModule]
})
export class DirectoryCompanionGuidedComponent {
  /** Current provider-directory form values (drives skip-if-set). */
  readonly formValues = input<Partial<IFilterFormValues> | null>(null);

  /**
   * True while the current search has results (or none has completed yet). When
   * it goes false — a completed search returned zero providers — the zero-results
   * "de-select chips" state replaces the questionnaire so the user can loosen
   * their filters; it reverts automatically once results return.
   */
  readonly hasResults = input(true);

  /**
   * Whether to render the component's own close button. A host that provides its
   * own close affordance (e.g. the mobile bottom sheet, which has its own X in
   * the sheet header) sets this false so the user isn't shown two close buttons.
   */
  readonly showCloseButton = input(true);

  /**
   * Whether to render the component's own back link. The mobile bottom sheet
   * hoists the back control into its header (inline with the collapse chevron),
   * so it sets this false to avoid a duplicate back link in the body.
   */
  readonly showBackButton = input(true);

  /** Emits the filter patch to apply when the user advances a step. */
  readonly filtersApplied = output<Partial<IFilterFormValues>>();
  /** Emitted when the sidebar should close. */
  readonly closed = output();

  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);
  // Guided-flow Segment events (MER-4945) are fired straight from the component
  // via SegmentService, rather than bubbling an output per event up to the host.
  private readonly segmentService = inject<SegmentService<SondermindEvents>>(SegmentService);

  /** Question heading, focused on step change for screen-reader users. */
  readonly questionHeading = viewChild<ElementRef<HTMLHeadingElement>>('questionHeading');
  /** Zero-results heading, focused when that state activates so the swap is announced. */
  readonly zeroResultsHeading = viewChild<ElementRef<HTMLHeadingElement>>('zeroResultsHeading');
  /** Feedback heading, focused when the terminal feedback screen activates. */
  readonly feedbackHeading = viewChild<ElementRef<HTMLHeadingElement>>('feedbackHeading');
  /** Thumbs-up confirmation heading, focused when the "next step" view swaps in. */
  readonly feedbackConfirmHeading = viewChild<ElementRef<HTMLHeadingElement>>('feedbackConfirmHeading');
  /** De-select heading, focused when thumbs-down swaps in the loosen-filters view. */
  readonly feedbackDeselectHeading = viewChild<ElementRef<HTMLHeadingElement>>('feedbackDeselectHeading');

  constructor() {
    // Move focus to the zero-results heading when that state activates. The
    // questionnaire and zero-results branches swap wholesale, so without this
    // the focused control (a chip or Next) is destroyed and focus falls back to
    // <body>, silently dropping keyboard/screen-reader users.
    effect(() => {
      if (!this.hasResults()) {
        this.focusHeading(this.zeroResultsHeading);
      }
    });

    // Same rationale for the terminal feedback screen: when the questionnaire is
    // complete and swaps to the "Did you find what you're looking for?" screen,
    // move focus to its heading. Guard on hasResults so a zero-results search
    // (which takes priority in the template) keeps its own focus.
    effect(() => {
      if (this.isComplete() && this.hasResults()) {
        this.focusHeading(this.feedbackHeading);
      }
    });

    // Thumbs-down swaps the thumbs controls out for the loosen-filters view. The
    // focused thumb button is destroyed, so move focus to the de-select heading
    // to keep keyboard/screen-reader users oriented (same rationale as above).
    effect(() => {
      if (this.showFeedbackDeselect()) {
        this.focusHeading(this.feedbackDeselectHeading);
      }
    });

    // Thumbs-up swaps the thumbs controls out for the "next step" confirmation.
    // Move focus to its heading for the same reason as the thumbs-down swap.
    effect(() => {
      if (this.showFeedbackConfirm()) {
        this.focusHeading(this.feedbackConfirmHeading);
      }
    });

    // Analytics: fire GuidedFlowZeroResultsShown on the rising edge of the
    // zero-results state (a completed 0-result search) so it tracks once per
    // occurrence, not on every recompute while the state stays visible.
    effect(() => {
      const isShown = !this.hasResults();
      if (isShown && !this.zeroResultsWasTracked) {
        this.zeroResultsWasTracked = true;
        this.track<SondermindEvent.GuidedFlowZeroResultsShown>(
          SondermindEvent.GuidedFlowZeroResultsShown,
          { appliedFilters: this.appliedFilterChips().map((chip) => chip.title) }
        );
      } else if (!isShown) {
        this.zeroResultsWasTracked = false;
      }
    });

    // Fire GuidedFlowCompleted once on entering the terminal state (reached via
    // Next or auto-advance), and reset feedback so re-entry starts neutral.
    effect(() => {
      const isDone = this.isComplete();
      if (isDone && !this.completedWasTracked) {
        this.completedWasTracked = true;
        this.feedbackResponse.set(null);
        this.track<SondermindEvent.GuidedFlowCompleted>(
          SondermindEvent.GuidedFlowCompleted,
          { answeredCount: this.answeredQuestionCount() }
        );
      } else if (!isDone) {
        this.completedWasTracked = false;
      }
    });

    // Fire GuidedFlowQuestionAnswered for each answered question left behind as the
    // step moves forward (covers both Next and auto-advance); skipped-empty ones don't.
    effect(() => {
      const index = this.currentStepIndex();
      const from = this.lastTrackedStepIndex;
      this.lastTrackedStepIndex = index;
      if (from === null || index <= from) {
        return;
      }
      this.steps.slice(from, Math.min(index, this.totalSteps)).forEach((question) => {
        if (this.isQuestionAnswered(question)) {
          this.track<SondermindEvent.GuidedFlowQuestionAnswered>(
            SondermindEvent.GuidedFlowQuestionAnswered,
            { questionId: question.id, selectedChips: this.selectedChipTitlesForStep(question) }
          );
        }
      });
    });

    // afterNextRender is browser-only (SSR-safe): fires GuidedFlowModuleViewed once per mount.
    afterNextRender({ write: () => this.trackModuleViewed() }, { injector: this.injector });
  }

  /** Guards the once-per-mount GuidedFlowStarted event (first chip toggle or Next). */
  private hasStarted = false;
  /** Rising-edge guard for the GuidedFlowZeroResultsShown event. */
  private zeroResultsWasTracked = false;
  /** Rising-edge guard for the GuidedFlowCompleted event. */
  private completedWasTracked = false;
  /** Last step index reported to GuidedFlowQuestionAnswered. */
  private lastTrackedStepIndex: number | null = null;

  readonly iconSizes = IconSizes;
  readonly irisIconButtonTypes = IrisIconButtonTypes;

  /** Care-coordinator support line shown in the footers (shared with the support card). */
  readonly supportPhoneLink = LandingPageContact.MATCH_BY_PHONE_LINK;
  readonly supportPhoneDisplay = LandingPageContact.MATCH_BY_PHONE_DISPLAY;

  /**
   * The ordered question list. Every question is always shown so the user can
   * review or change any dimension; a question whose filter is already set
   * simply renders with its chips pre-selected (see `isChipSelected`).
   */
  readonly steps: IGuidedQuestion[] = GUIDED_QUESTIONS;

  /**
   * Visible step, recomputed from the filters on every change so it auto-advances
   * past an answered question and skips already-set ones. Once all remaining
   * questions are answered it falls back to the first unanswered one overall, so
   * de-selecting a filter pulls an earlier question back instead of stranding the
   * user on the feedback screen. Next/Back/Start over write the index directly.
   */
  readonly currentStepIndex = linkedSignal<Partial<IFilterFormValues> | null, number>({
    source: () => this.formValues(),
    computation: (formValues, previous) => {
      const forward = this.firstUnansweredFrom(previous?.value ?? 0);
      return forward < this.totalSteps ? forward : this.firstUnansweredFrom(0);
    }
  });

  readonly currentStep = computed<IGuidedQuestion | null>(
    () => this.steps[this.currentStepIndex()] ?? null
  );
  readonly totalSteps = this.steps.length;
  readonly isComplete = computed(() => this.currentStepIndex() >= this.totalSteps);
  readonly canGoBack = computed(() => this.currentStepIndex() > 0);

  /**
   * The user's response on the terminal feedback screen, or `null` before they
   * answer. `UP` swaps in the "you're ready for the next step" confirmation;
   * `DOWN` reveals the de-select chips so the user can loosen filters. Firing
   * the Segment feedback event is handled separately (MER-4945). Reset to `null`
   * on entering/leaving the terminal state so the screen always re-opens neutral.
   */
  readonly feedbackResponse = signal<GuidedFeedbackResponse | null>(null);

  /** True once the user gives thumbs-up: show the "next step" confirmation view. */
  readonly showFeedbackConfirm = computed(
    () => this.feedbackResponse() === GuidedFeedbackResponse.UP
  );

  /** True once the user gives thumbs-down: reveal the de-select chips affordance. */
  readonly showFeedbackDeselect = computed(
    () => this.feedbackResponse() === GuidedFeedbackResponse.DOWN
  );

  /**
   * A short, human-readable summary of the applied filters, shown under the
   * feedback prompt (e.g. "Anxiety or worry, Female and Aetna"). Empty when only
   * the required state filter is set.
   */
  readonly appliedFilterSummary = computed<string>(() => {
    const titles = this.appliedFilterChips().map((chip) => chip.title);
    if (titles.length <= 1) {
      return titles[0] ?? '';
    }
    return `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
  });

  /**
   * The primary heading for the current state, exposed so a host chrome (e.g. the
   * mobile bottom sheet's collapsed peek row) can summarize the flow without
   * reaching into the component's internal step state. Mirrors the visible
   * headings the template renders across the questionnaire, zero-results, and
   * terminal feedback states.
   */
  readonly peekTitle = computed<string>(() => {
    if (!this.hasResults()) {
      return 'No exact matches';
    }
    if (this.isComplete()) {
      if (this.showFeedbackConfirm()) {
        return "You're ready for the next step";
      }
      if (this.showFeedbackDeselect()) {
        return "Let's keep searching";
      }
      return "Did you find what you're looking for?";
    }
    return this.currentStep()?.title ?? '';
  });

  /** Tags currently set on the form, per filter key — the source of truth for chip selection. */
  private readonly selectedTagsByKey = computed<Map<keyof IFilterFormValues, Set<string>>>(() => {
    const map = new Map<keyof IFilterFormValues, Set<string>>();
    const values = this.formValues() ?? {};
    (Object.keys(values) as Array<keyof IFilterFormValues>).forEach((key) => {
      // Use the same array/scalar classification as the write path (toggleChip)
      // so read and write never disagree about a filter's shape.
      const tags = GUIDED_ARRAY_FILTER_KEYS.has(key)
        ? this.arrayTags(values[key])
        : this.scalarTag(values[key]);
      if (tags.length) {
        map.set(key, new Set(tags));
      }
    });
    return map;
  });

  /** A chip is selected when all of its values are present in the current form filters. */
  isChipSelected(chip: IFindMyFitChip): boolean {
    if (!chip.formKey || !chip.values.length) {
      return false;
    }
    const selectedTags = this.selectedTagsByKey().get(chip.formKey);
    if (!selectedTags) {
      return false;
    }
    return chip.values.every((option) => selectedTags.has(option.tag));
  }

  /**
   * Toggle a chip by applying/removing its values on the form immediately. The
   * emitted patch is the full next value for that filter key (add or remove),
   * so the host's `updateFormValues` reconciles it and the derived selection
   * updates automatically. A `singleSelect` chip replaces the whole array value
   * for its key instead of accumulating.
   */
  toggleChip(chip: IFindMyFitChip): void {
    if (!chip.formKey || !chip.values.length) {
      return;
    }
    const key = chip.formKey;
    const isSelected = this.isChipSelected(chip);

    // Analytics: a chip toggle is a first-class engagement signal. `selected` is
    // the resulting state (the inverse of the current one), and the toggle can
    // only happen on the questionnaire surface.
    this.trackStarted();
    this.trackFilterChanged({
      filterKey: key,
      tag: chip.values[0].tag,
      selected: !isSelected,
      context: GuidedFlowFilterContext.QUESTIONNAIRE,
      questionId: this.currentStep()?.id
    });

    if (GUIDED_ARRAY_FILTER_KEYS.has(key)) {
      const current = this.arrayValues(this.formValues()?.[key]);
      const chipTags = new Set(chip.values.map((option) => option.tag));
      let next: IFilterFormOptionValue[];
      if (isSelected) {
        next = current.filter((option) => !chipTags.has(option.tag));
      } else if (chip.singleSelect) {
        // Replace any existing selection for this dimension.
        next = [...chip.values];
      } else {
        next = [...current, ...chip.values.filter((option) => !current.some((c) => c.tag === option.tag))];
      }
      this.filtersApplied.emit({ [key]: next } as Partial<IFilterFormValues>);
    } else {
      // Scalar filter (e.g. serviceType, insurance): set the value, or clear it
      // when toggling off. Single-select is inherent to scalar keys.
      this.filtersApplied.emit({ [key]: isSelected ? null : chip.values[0] } as Partial<IFilterFormValues>);
    }
  }

  /**
   * The currently-applied filters flattened to one removable chip per value,
   * shown in the zero-results state. Array dimensions expand to one chip per
   * option; scalar filters (insurance, serviceType) contribute a single chip.
   * `state` is excluded — it is required and cannot be removed.
   */
  readonly appliedFilterChips = computed<IAppliedFilterChip[]>(() => {
    const values = this.formValues() ?? {};
    const chips: IAppliedFilterChip[] = [];

    // Iterate the ordered key sets (not `Object.keys(values)`) so chip order is
    // deterministic and independent of form-value insertion order: all array
    // dimensions first, then the removable scalars.
    GUIDED_ARRAY_FILTER_KEYS.forEach((key) => {
      this.arrayValues(values[key]).forEach((option) => {
        chips.push({ title: option.title, formKey: key, tag: option.tag });
      });
    });

    GUIDED_REMOVABLE_SCALAR_KEYS.forEach((key) => {
      const option = this.scalarOption(values[key]);
      if (option) {
        chips.push({ title: option.title, formKey: key, tag: option.tag });
      }
    });

    return chips;
  });

  /**
   * Remove a single applied filter and re-emit the reduced value for its key so
   * the host's `updateFormValues` re-runs the search. Array keys drop the value
   * with the matching tag; removable scalar keys are cleared to `null`. Any other
   * key is ignored — only the dimensions surfaced by `appliedFilterChips` (which
   * excludes the required `state`) are removable.
   */
  onRemoveChip(chip: IAppliedFilterChip): void {
    const key = chip.formKey;

    // Analytics: removing a chip is always a de-selection. It surfaces in two
    // places — the zero-results state and the thumbs-down "loosen filters" view;
    // report which so the funnel can tell recovery paths apart.
    this.trackFilterChanged({
      filterKey: key,
      tag: chip.tag,
      selected: false,
      context: !this.hasResults()
        ? GuidedFlowFilterContext.ZERO_RESULTS
        : GuidedFlowFilterContext.FEEDBACK_DESELECT
    });

    if (GUIDED_ARRAY_FILTER_KEYS.has(key)) {
      const next = this.arrayValues(this.formValues()?.[key]).filter((option) => option.tag !== chip.tag);
      this.filtersApplied.emit({ [key]: next } as Partial<IFilterFormValues>);
    } else if (GUIDED_REMOVABLE_SCALAR_KEYS.has(key)) {
      this.filtersApplied.emit({ [key]: null } as Partial<IFilterFormValues>);
    }
  }

  onNext(): void {
    // QuestionAnswered/Completed fire from the forward-transition and completion effects.
    this.trackStarted();
    this.currentStepIndex.set(this.firstUnansweredFrom(this.currentStepIndex() + 1));
    this.focusQuestionHeading();
  }

  onBack(): void {
    // Leaving the terminal feedback screen resets the response so returning to
    // it starts fresh (no stale de-select chips or acknowledgement).
    if (this.isComplete()) {
      this.feedbackResponse.set(null);
    }
    this.currentStepIndex.update((index) => Math.max(index - 1, 0));
    this.focusQuestionHeading();
  }

  /** First unanswered question at or after `start`, or `totalSteps` if none remain. */
  private firstUnansweredFrom(start: number): number {
    const from = Math.max(0, start);
    const offset = this.steps.slice(from).findIndex((question) => !this.isQuestionAnswered(question));
    return offset === -1 ? this.totalSteps : from + offset;
  }

  /** True once at least one chip on the question is reflected in the form values. */
  private isQuestionAnswered(question: IGuidedQuestion): boolean {
    return question.chips.some((chip) => this.isChipSelected(chip));
  }

  /**
   * Record the user's answer on the terminal feedback screen. Thumbs-down
   * reveals the de-select chips so the user can loosen their filters, and either
   * answer fires GuidedFlowFeedbackSubmitted (MER-4945).
   */
  onFeedback(positive: boolean): void {
    this.feedbackResponse.set(
      positive ? GuidedFeedbackResponse.UP : GuidedFeedbackResponse.DOWN
    );
    this.track<SondermindEvent.GuidedFlowFeedbackSubmitted>(
      SondermindEvent.GuidedFlowFeedbackSubmitted,
      { positive, appliedFilterSummary: this.appliedFilterSummary() }
    );
  }

  /**
   * Restart the guided flow from the first question: clear the feedback response,
   * return to step 0, and reset every removable filter (array dimensions to `[]`,
   * removable scalars to `null`) in a single patch so the host re-runs the search
   * once. The required `state` filter is preserved.
   */
  onStartOver(): void {
    const reset: Record<string, IFilterFormOptionValue[] | null> = {};
    GUIDED_ARRAY_FILTER_KEYS.forEach((key) => {
      reset[key] = [];
    });
    GUIDED_REMOVABLE_SCALAR_KEYS.forEach((key) => {
      reset[key] = null;
    });
    this.filtersApplied.emit(reset as Partial<IFilterFormValues>);
    this.track<SondermindEvent.GuidedFlowRestarted>(SondermindEvent.GuidedFlowRestarted, {});

    this.feedbackResponse.set(null);
    this.currentStepIndex.set(0);
    this.focusQuestionHeading();
  }

  onClose(): void {
    this.track<SondermindEvent.GuidedFlowExited>(SondermindEvent.GuidedFlowExited, {});
    this.closed.emit();
  }

  /** Track GuidedFlowStarted exactly once, on the first engagement (chip toggle or Next). */
  private trackStarted(): void {
    if (this.hasStarted) {
      return;
    }
    this.hasStarted = true;
    this.track<SondermindEvent.GuidedFlowStarted>(SondermindEvent.GuidedFlowStarted, {});
  }

  private trackModuleViewed(): void {
    this.track<SondermindEvent.GuidedFlowModuleViewed>(SondermindEvent.GuidedFlowModuleViewed, {});
  }

  private trackFilterChanged(change: IGuidedFlowFilterChange): void {
    this.track<SondermindEvent.GuidedFlowFilterChanged>(SondermindEvent.GuidedFlowFilterChanged, {
      filterKey: change.filterKey,
      tag: change.tag,
      selected: change.selected,
      context: change.context,
      questionId: change.questionId
    });
  }

  /**
   * Fire a guided-flow Segment event, injecting the constant `project`/`variant`
   * and swallowing errors. SSR-guarded — no-op on the server. Callers pass only
   * the event-specific fields.
   */
  private track<E extends keyof SondermindEvents>(
    event: E,
    data: Omit<SondermindEvents[E], 'project' | 'variant'>
  ): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.segmentService.track<E>(event, {
      project: GUIDED_FLOW_PROJECT,
      variant: GUIDED_FLOW_VARIANT,
      ...data
    } as SondermindEvents[E]).catch(() => {});
  }

  /** Titles of the chips currently selected for a given question. */
  private selectedChipTitlesForStep(question: IGuidedQuestion): string[] {
    return question.chips.filter((chip) => this.isChipSelected(chip)).map((chip) => chip.title);
  }

  /** Count of guided questions that have at least one chip selected. */
  private answeredQuestionCount(): number {
    return this.steps.filter((question) => this.selectedChipTitlesForStep(question).length > 0).length;
  }

  private focusQuestionHeading(): void {
    this.focusHeading(this.questionHeading);
  }

  private focusHeading(heading: () => ElementRef<HTMLHeadingElement> | undefined): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    afterNextRender(
      { write: () => heading()?.nativeElement.focus() },
      { injector: this.injector }
    );
  }

  private arrayValues(value: IFilterFormValues[keyof IFilterFormValues] | undefined): IFilterFormOptionValue[] {
    return Array.isArray(value) ? (value as IFilterFormOptionValue[]) : [];
  }

  private arrayTags(value: IFilterFormValues[keyof IFilterFormValues] | undefined): string[] {
    return this.arrayValues(value).map((option) => option.tag);
  }

  private scalarTag(value: IFilterFormValues[keyof IFilterFormValues] | undefined): string[] {
    const option = this.scalarOption(value);
    return option ? [option.tag] : [];
  }

  /** Narrow a scalar filter value (insurance, serviceType) to a filter option, or null. */
  private scalarOption(
    value: IFilterFormValues[keyof IFilterFormValues] | undefined
  ): IFilterFormOptionValue | null {
    if (value && typeof value === 'object' && 'title' in value && 'tag' in value) {
      return value as IFilterFormOptionValue;
    }
    return null;
  }
}
